import { BaseService } from './BaseService';

/**
 * Interfaces para las estadísticas del jugador
 */
export interface EstadisticasEntrenamiento {
  totalDias: number;
  asistencias: number;
  faltas: number;
  porcentajeAsistencia: number;
}

export interface EstadisticasTitularidades {
  totalJornadas: number;
  titularidades: number;
  porcentajeTitularidad: number; // Cambiar de porcentajeTitularidades a porcentajeTitularidad
}

/**
 * Interfaz para las estadísticas de un jugador en el contexto de equipo
 */
export interface EstadisticasJugadorEquipo {
  jugadorId: number;
  nombre: string;
  apellido: string;
  totalJornadas: number;
  titularidades: number;
  porcentajeTitularidad: number;
  minutosTotal: number;
  promedioMinutosPorPartido: number;
  porcentajeMinutosTotales: number;
}

export interface EstadisticasMinutos {
  minutosTotal: number;
  promedioMinutosPorPartido: number;
  partidosJugados: number;
  porcentajeMinutosTotales: number; // Agregar este campo
}

export interface RegistroFisicoEvolucion {
  fecha: string; // LocalDate se serializa como string
  altura: number;
  peso: number;
  imc: number;
  temporada: string;
}

export interface EstadisticasCompletas {
  entrenamiento: EstadisticasEntrenamiento;
  titularidades: EstadisticasTitularidades;
  minutos: EstadisticasMinutos;
  evolucionFisica: RegistroFisicoEvolucion[];
}

/**
 * Interfaz para los datos de minutos por jornada
 */
export interface MinutosPorJornada {
  jornada: number;
  minutosJugador: number;
  promedioEquipo: number;
  rival?: string;
}

/**
 * Servicio para obtener estadísticas completas del jugador
 */
class EstadisticasService extends BaseService {
  constructor() {
    super('/estadisticas');
  }

  /**
   * Obtiene estadísticas de entrenamiento para un jugador en una temporada
   * @param jugadorId ID del jugador
   * @param temporadaId ID de la temporada
   * @returns Estadísticas de entrenamiento
   */
  async obtenerEstadisticasEntrenamiento(
    jugadorId: number, 
    temporadaId: number
  ): Promise<EstadisticasEntrenamiento> {
    const response = await this.get<EstadisticasEntrenamiento>(
      `jugador/${jugadorId}/entrenamiento/temporada/${temporadaId}`
    );
    return response.data;
  }

  /**
   * Obtiene estadísticas de titularidades para un jugador en una temporada
   * @param jugadorId ID del jugador
   * @param temporadaId ID de la temporada
   * @returns Estadísticas de titularidades
   */
  async obtenerEstadisticasTitularidades(
    jugadorId: number, 
    temporadaId: number
  ): Promise<EstadisticasTitularidades> {
    const response = await this.get<EstadisticasTitularidades>(
      `jugador/${jugadorId}/titularidades/temporada/${temporadaId}`
    );
    return response.data;
  }

  /**
   * Obtiene estadísticas de minutos para un jugador en una temporada
   * @param jugadorId ID del jugador
   * @param temporadaId ID de la temporada
   * @param equipoNombre Nombre del equipo para determinar la categoría
   * @returns Estadísticas de minutos
   */
  async obtenerEstadisticasMinutos(
    jugadorId: number, 
    temporadaId: number,
    equipoNombre: string
  ): Promise<EstadisticasMinutos> {
    const response = await this.get<EstadisticasMinutos>(
      `jugador/${jugadorId}/minutos/temporada/${temporadaId}?equipoNombre=${encodeURIComponent(equipoNombre)}`
    );
    return response.data;
  }

  /**
   * Obtiene la evolución física del jugador (altura, peso, IMC)
   * @param jugadorId ID del jugador
   * @returns Array con la evolución física ordenada por fecha
   */
  async obtenerEvolucionFisica(jugadorId: number): Promise<RegistroFisicoEvolucion[]> {
    const response = await this.get<EvolucionFisicaResponse>(
      `jugador/${jugadorId}/evolucion-fisica`
    );
    // Extraer el array de registros de la respuesta
    return response.data.registros || [];
  }

  /**
   * Obtiene todas las estadísticas completas del jugador
   * @param jugadorId ID del jugador
   * @param temporadaId ID de la temporada
   * @param equipoNombre Nombre del equipo
   * @returns Estadísticas completas
   */
  async obtenerEstadisticasCompletas(
    jugadorId: number, 
    temporadaId: number,
    equipoNombre: string
  ): Promise<EstadisticasCompletas> {
    const [entrenamiento, titularidades, minutos, evolucionFisica] = await Promise.all([
      this.obtenerEstadisticasEntrenamiento(jugadorId, temporadaId),
      this.obtenerEstadisticasTitularidades(jugadorId, temporadaId),
      this.obtenerEstadisticasMinutos(jugadorId, temporadaId, equipoNombre),
      this.obtenerEvolucionFisica(jugadorId)
    ]);

    return {
      entrenamiento,
      titularidades,
      minutos,
      evolucionFisica
    };
  }

  /**
   * Obtiene los minutos por jornada del jugador y promedio del equipo
   * @param jugadorId ID del jugador
   * @param temporadaId ID de la temporada
   * @returns Promise con los datos de minutos por jornada
   */
  async obtenerMinutosPorJornada(jugadorId: number, temporadaId: number): Promise<MinutosPorJornada[]> {
    const response = await this.get<MinutosPorJornada[]>(`/jugador/${jugadorId}/minutos-jornada/temporada/${temporadaId}`);
    return response.data;
  }

  /**
   * Obtiene estadísticas de todos los jugadores de un equipo en una temporada
   * @param equipoId ID del equipo
   * @param temporadaId ID de la temporada
   * @returns Lista de estadísticas de jugadores del equipo
   */
  async obtenerEstadisticasEquipo(
    equipoId: number,
    temporadaId: number
  ): Promise<EstadisticasJugadorEquipo[]> {
    const response = await this.get<EstadisticasJugadorEquipo[]>(
      `equipo/${equipoId}/temporada/${temporadaId}`
    );
    return response.data;
  }

  /**
   * Obtiene las fechas específicas donde un jugador faltó a entrenamientos
   * @param jugadorId ID del jugador
   * @param temporadaId ID de la temporada
   * @returns Promise con las fechas de faltas
   */
  async obtenerFechasFaltas(jugadorId: number, temporadaId: number): Promise<string[]> {
    const response = await fetch(`${this.baseURL}/estadisticas/jugador/${jugadorId}/faltas/temporada/${temporadaId}`, {
      method: 'GET',
      headers: this.getHeaders(), // ✅ Usar getHeaders() que incluye el token de autenticación
    });

    if (!response.ok) {
      throw new Error(`Error al obtener fechas de faltas: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data || [];
  }

  /**
   * Calcula el IMC basado en altura y peso
   * @param altura Altura en centímetros
   * @param peso Peso en kilogramos
   * @returns IMC calculado
   */
  static calcularIMC(altura: number, peso: number): number {
    const alturaEnMetros = altura / 100;
    return peso / (alturaEnMetros * alturaEnMetros);
  }

  /**
   * Determina la duración del partido según la categoría del equipo
   * @param equipoNombre Nombre del equipo
   * @returns Duración en minutos
   */
  static obtenerDuracionPartido(equipoNombre: string): number {
    if (!equipoNombre) return 60;
    const categoria = equipoNombre.toUpperCase();
    
    // S7, S8, S9 -> 48 min
    if (categoria.includes('S7') || categoria.includes('S8') || categoria.includes('S9') || categoria.includes('BENJAMIN FEMENINO')) {
      return 48;
    } 
    
    // S10, S11, S12 -> 60 min
    if (categoria.includes('S10') || categoria.includes('S11') || categoria.includes('S12') || categoria.includes('ALEVIN FEMENINO')) {
      return 60;
    }

    // S13, S14, Infantil Femenino -> 70 min
    if (categoria.includes('S13') || categoria.includes('S14') || categoria.includes('INFANTIL FEMENINO')) {
      return 70;
    }

    // S15, S16, Cadete Femenino -> 80 min
    if (categoria.includes('S15') || categoria.includes('S16') || categoria.includes('CADETE FEMENINO')) {
      return 80;
    }

    // Juvenil, Amateur Femenino (Primer Equipo) -> 90 min
    if (categoria.includes('JUVENIL') || categoria.includes('AMATEUR FEMENINO') || categoria.includes('PRIMER EQUIPO')) {
      return 90;
    }
    
    // Por defecto, asumir 60 minutos
    return 60;
  }
}

// Exportar instancia del servicio
export const estadisticasService = new EstadisticasService();
export default EstadisticasService;

// Actualizar la interfaz para coincidir con el backend
export interface EvolucionFisicaResponse {
  registros: RegistroFisicoEvolucion[];
}

